import {DB_COLLECTION_NAMES, TIME_FORMATS} from "constants/AppConstants.js";
import {DbCollectionFactory} from "factories/DbCollectionFactory";
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import ConvertUtil from "utils/ConvertUtil.js";
import {isNil} from "lodash";
import {useAppUser} from "contexts/AppUserContext";
import DateTimeUtil from "utils/DateTimeUtil";
import {LocalDbService} from "services/LocalDBService";
import {isAndroid} from "FirebaseUtils";
import LogService from "services/LogService";
import * as Promise from "bluebird";
import {syncLocalRecordingFiles} from "services/syncLocalService";
import {reportDiaryActivityCount, setupDiaryActivityCountSync} from "utils/diaryActivityCountSync";
import type {NavigationsProviderProps} from "./NavigationsContext.types.js";

Promise.config({cancellation: true});

const NavigationsContext = createContext<any>(undefined);
const navCollection: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.navs);
const pointCollection: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.points);
const userCollection: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.users);

// updateKeys partitions navs into upload/download buckets.
// Per-nav logging is intentionally avoided here: on Android each forced log call
// triggers Capacitor bridge calls (Network/App state) which makes large libraries
// pause the UI for several seconds during the compare pass.
const updateKeys = (localNav, remoteNav, toUpdateLocalDBKeys, toUpdateRemoteDBKeys) => {
  if (!localNav) {
    toUpdateLocalDBKeys.push(remoteNav.key);
    return;
  }

  if (remoteNav && remoteNav.ID && localNav.lmd < remoteNav.lmd) {
    toUpdateLocalDBKeys.push(remoteNav.key);
    return;
  }

  if (!remoteNav || localNav.lmd > remoteNav.lmd || remoteNav.fileSynced === false || !remoteNav.ID) {
    if (localNav.ID) {
      toUpdateRemoteDBKeys.push(localNav.ID);
    }
  }
};

const fixDuplicatePoints = (pointsArray) => {
  if (!Array.isArray(pointsArray)) return pointsArray;

  const seenM = new Set();

  return pointsArray.map((point) => {
    const newPoint = {...point};

    // Fix duplicate m
    if (newPoint.m) {
      const baseM = newPoint.m;
      let counter = 1;
      while (seenM.has(newPoint.m)) {
        newPoint.m = `${baseM}_${counter}`;
        counter++;
      }
      seenM.add(newPoint.m);
    }

    return newPoint;
  });
};

function NavigationsProvider({
  children,
  userId,
  networkStatus,
  isDiaryActive,
  diaryActiveTick,
  cancelAllPromises,
  addPromisedSyncRef
}: NavigationsProviderProps) {
  const {appUser} = useAppUser();
  const [navigations, setNavigations] = useState([]);
  const [isNavigationsLoaded, setIsNavigationsLoaded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [forceReload, setForceReload] = useState(0);
  const [navToUpload, setNavToUpload] = useState(0);
  const [navToDownload, setNavToDownload] = useState(0);

  const navigationsRef = useRef(navigations);
  const progressRemainingRef = useRef({upload: 0, download: 0});
  const progressIntervalRef = useRef(null);
  const lastLocalNavForSyncRef = useRef(null);
  const syncInProgressRef = useRef(false);
  const pendingCloudSyncRef = useRef(false);
  const cloudSyncDebounceRef = useRef(null);
  const attemptCloudSyncRef = useRef<any>(async () => {});
  const scheduleCloudSyncRef = useRef<any>(() => {});

  useEffect(() => {
    if (!isAndroid) return;
    reportDiaryActivityCount(navigations.length);
  }, [navigations]);

  useEffect(() => {
    if (!isAndroid) return;

    let listener;
    (async () => {
      listener = await setupDiaryActivityCountSync(() => navigationsRef.current.length);
    })();

    return () => {
      listener?.remove();
    };
  }, []);

  const addNavigationsToState = useCallback((_navigations, withPrevious = false, sort = false) => {
    let navigationsArray = Array.isArray(_navigations) ? _navigations : [];

    if (withPrevious) {
      const existingIds = new Set(navigationsRef.current.map((n) => n.ID));
      navigationsArray = navigationsArray.filter((nav) => nav.ID && !existingIds.has(nav.ID));
    }

    navigationsArray = navigationsArray.filter((navigation) => {
      const _isNil = isNil(navigation.isDel);
      const _isNotPermanentlyDeleted = isAndroid || isNil(navigation.isDelPer) || !navigation.isDelPer;
      return (
        (_isNil || (!_isNil && !navigation.isDel)) &&
        (isNil(navigation.sv) || navigation.sv) &&
        _isNotPermanentlyDeleted
      );
    });

    navigationsRef.current = withPrevious ? [...navigationsArray, ...navigationsRef.current] : [...navigationsArray];

    if (sort) {
      setNavigations(
        navigationsRef.current.sort((a, b) => {
          if (!a.dS) return 1;
          if (!b.dS) return -1;
          return b.dS - a.dS;
        })
      );
    } else {
      setNavigations(navigationsRef.current);
    }
  }, []);

  const deleteExamplesActivities = async () => {
    const db = await LocalDbService.getLocalDbService();
    const localNavigations = await db.getNavigations();
    const examplesActivities = localNavigations.filter((nav) => nav.ID.includes("demo_"));
    examplesActivities.map((nav) => nav.ID).forEach((id) => db.deleteNavigationById(id));
  };

  const fetchLocalData = async () => {
    const db = await LocalDbService.getLocalDbService();
    const localNavigations = await db.getNavigations();
    addNavigationsToState(localNavigations, false, true);
    setIsNavigationsLoaded(true);
    return localNavigations;
  };

  const refreshLocalNavigations = useCallback(async () => {
    if (!isAndroid || !appUser?.uid) {
      return;
    }
    await fetchLocalData();
  }, [appUser?.uid, addNavigationsToState]);

  useEffect(() => {
    const currentUserId = appUser?.uid;
    let cancelled = false;

    const importRecordingFilesInBackground = async (userIdForImport, localNavMap, navLength, isCancelled) => {
      try {
        await syncLocalRecordingFiles(addNavigationsToState, userIdForImport, navLength);

        if (isCancelled()) {
          return;
        }

        if (localNavMap.size > 0) {
          await deleteExamplesActivities();
        }

        if (!isCancelled()) {
          await fetchLocalData();
        }
      } catch (error) {
        (LogService.addErrorLogAsync as any)("", "Background recording file sync failed", error, "error", false, true);
      }
    };

    const init = async () => {
      if (currentUserId) {
        if (isAndroid) {
          const nav = await fetchLocalData();
          if (cancelled) {
            return;
          }

          const localNavMap = new Map(
            nav
              .filter((nav) => nav.ID != null && nav.isDel === false && !nav.ID.includes("demo_"))
              .map((nav) => [nav.ID, nav])
          );

          lastLocalNavForSyncRef.current = nav;

          if (!cancelled) {
            pendingCloudSyncRef.current = true;
            scheduleCloudSyncRef.current(nav);
          }

          // Recording import runs in the background; it's an I/O job that should not delay sync or UI.
          void importRecordingFilesInBackground(currentUserId, localNavMap, nav.length, () => cancelled);
        } else {
          fetchData();
        }
      } else {
        pendingCloudSyncRef.current = false;
        setIsNavigationsLoaded(true);
      }
    };
    init();

    return () => {
      cancelled = true;
      if (cloudSyncDebounceRef.current) {
        clearTimeout(cloudSyncDebounceRef.current);
        cloudSyncDebounceRef.current = null;
      }
      if (currentUserId) {
        if (!isAndroid) {
          setNavigations([]);
        } else {
          LogService.addInfoLogAsync(currentUserId, "NavigationsProvider unmounted, canceling sync promises");
          cancelAllPromises();
        }
      }
    };
  }, [appUser?.uid, forceReload]);

  const canSync = () => isDiaryActive.current && networkStatus;

  const attemptCloudSync = async (localNavigations) => {
    if (!isAndroid || !appUser?.uid) {
      return;
    }

    const nav = localNavigations ?? lastLocalNavForSyncRef.current;
    if (!nav) {
      pendingCloudSyncRef.current = true;
      return;
    }
    lastLocalNavForSyncRef.current = nav;

    if (syncInProgressRef.current) {
      pendingCloudSyncRef.current = true;
      return;
    }

    if (!canSync()) {
      pendingCloudSyncRef.current = true;
      return;
    }

    pendingCloudSyncRef.current = false;
    await syncLocalDBWithFirebase(nav);
  };

  attemptCloudSyncRef.current = attemptCloudSync;

  // Coalesce sync requests. If a debounce is already pending or a sync is in flight,
  // don't reset the timer — otherwise rapid upstream re-renders could keep deferring
  // the actual sync forever.
  scheduleCloudSyncRef.current = (localNavigations) => {
    if (!isAndroid || !appUser?.uid) {
      return;
    }
    if (localNavigations) {
      lastLocalNavForSyncRef.current = localNavigations;
    }
    if (syncInProgressRef.current || cloudSyncDebounceRef.current) {
      return;
    }
    cloudSyncDebounceRef.current = setTimeout(() => {
      cloudSyncDebounceRef.current = null;
      void attemptCloudSyncRef.current(lastLocalNavForSyncRef.current);
    }, 200);
  };

  const syncLocalDBWithFirebase = async (localNavigations) => {
    if (syncInProgressRef.current) {
      return;
    }

    LogService.addInfoLogAsync("", `Cloud sync started for user ${appUser?.uid}`, false, true);

    const navCollection: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.navs);
    const dbService = await LocalDbService.getLocalDbService();
    const now = new Date();

    if (!canSync()) {
      (LogService.addErrorLogAsync as any)("", "Cloud sync not possible", false, true);
      pendingCloudSyncRef.current = true;
      setIsSyncing(false);
      return;
    }

    syncInProgressRef.current = true;
    setIsSyncing(true);

    try {
      // 1. Fetch remote data
      const remoteNavigations = await fetchData();
      remoteNavigations.sort((a, b) => {
        if (!a.dS) return 1;
        if (!b.dS) return -1;
        return b.dS - a.dS;
      });
      LogService.addInfoLogAsync("", `Cloud sync - remoteNavigations: ${remoteNavigations.length}`, false, true);

      // 2. Process in chunks. Higher concurrency + a short yield keeps the UI
      // responsive while drastically reducing total wall-clock time for very
      // large libraries (e.g. several thousand downloads).
      const CHUNK_SIZE = isAndroid ? 20 : 10;
      const YIELD_MS = isAndroid ? 30 : 0;
      const processChunk = async (items, processor) => {
        for (let i = 0; i < items.length; i += CHUNK_SIZE) {
          const chunk = items.slice(i, i + CHUNK_SIZE);
          const results = await Promise.allSettled(chunk.map(processor));
          for (let idx = 0; idx < results.length; idx++) {
            if (results[idx].status === "rejected") {
              (LogService.addErrorLogAsync as any)(
                "",
                `Cloud sync item failed (chunk index ${idx})`,
                results[idx].reason,
                "error",
                false,
                true
              );
            }
          }
          if (YIELD_MS > 0) {
            await new Promise((resolve) => setTimeout(resolve, YIELD_MS));
          }
        }
      };

      // 3. Compare and prepare updates
      const toUpdateLocalDBKeys = [];
      const toUpdateRemoteDBKeys = [];
      const localNavMap = new Map(
        localNavigations.filter((nav) => nav.ID != null && !nav.ID.includes("demo_")).map((nav) => [nav.ID, nav])
      );

      // updateKeys is pure CPU work and runs in microseconds even for thousands of items,
      // so we do not need to yield between iterations.
      for (const remoteNav of remoteNavigations) {
        const localNav = localNavMap.get(remoteNav.key);
        updateKeys(localNav, remoteNav, toUpdateLocalDBKeys, toUpdateRemoteDBKeys);
        localNavMap.delete(remoteNav.key);
      }

      for (const [, localNav] of localNavMap) {
        updateKeys(localNav, null, toUpdateLocalDBKeys, toUpdateRemoteDBKeys);
      }

      const totalUpload = toUpdateRemoteDBKeys.length;
      const totalDownload = toUpdateLocalDBKeys.length;
      setNavToDownload(totalDownload);
      setNavToUpload(totalUpload);
      progressRemainingRef.current = {upload: totalUpload, download: totalDownload};

      // Yield before heavy upload/download so the diary list can paint and scroll.
      if (YIELD_MS > 0) {
        await new Promise((resolve) => setTimeout(resolve, YIELD_MS));
      }

      // Refresh the upload/download counters from the shared ref so the pull-to-refresh
      // header stays in sync with progress. We poll instead of calling setState in every
      // chunk worker so React only re-renders on a fixed cadence.
      const PROGRESS_INTERVAL_MS = 500;
      progressIntervalRef.current = setInterval(() => {
        const {upload, download} = progressRemainingRef.current;
        setNavToUpload((prev) => (prev !== upload ? upload : prev));
        setNavToDownload((prev) => (prev !== download ? download : prev));
      }, PROGRESS_INTERVAL_MS);

      // 4. Process remote updates in chunks
      const localNavLookup = new Map<any, any>(
        localNavigations.filter((nav) => nav.ID != null).map((nav) => [nav.ID, nav])
      );
      const remoteNavLookup = new Map<any, any>(remoteNavigations.map((nav) => [nav.key, nav]));

      await processChunk(toUpdateRemoteDBKeys, async (key) => {
        if (!canSync()) return;
        if (key.includes("demo_")) return;

        const nav = localNavLookup.get(key);
        if (!nav) {
          (LogService.addErrorLogAsync as any)(
            "",
            `Cloud sync upload: local nav not found for key ${key}`,
            "",
            "error",
            false,
            true
          );
          progressRemainingRef.current.upload = Math.max(0, progressRemainingRef.current.upload - 1);
          return;
        }
        const points = await dbService.getPointsByNavId(key);

        if (points && points.length > 1) {
          const cleanPoints = points.map(({nID, ...rest}) => rest);

          if (await pointCollection.setPointsByNavIdAsync(appUser?.uid, key, cleanPoints, true)) {
            nav.fileSynced = true;
            if (nav.ID) {
              await navCollection.addNavigationForCloudSync(appUser?.uid, nav, key);
            } else {
              (LogService.addErrorLogAsync as any)("", "Error while uploading not Nav ID", "", "error", false, true);
            }
          }
        } else {
          (LogService.addErrorLogAsync as any)("", "Error while uploading not enough points", "", "error", false, true);
        }
        progressRemainingRef.current.upload = Math.max(0, progressRemainingRef.current.upload - 1);
      });

      // 5. Process local updates in chunks. We push completed navigations to the
      // diary list in big batches so the user sees the list fill up during a
      // first-time sync without re-rendering on every single download.
      let demoCleanupDone = false;
      const navigationsBatch = [];
      const BATCH_FLUSH_SIZE = isAndroid ? 200 : 50;

      const flushBatch = () => {
        if (navigationsBatch.length === 0) {
          return;
        }
        addNavigationsToState(navigationsBatch.slice(), true, true);
        navigationsBatch.length = 0;
      };

      await processChunk(toUpdateLocalDBKeys, async (key) => {
        if (!canSync()) return;
        if (key.includes("demo_")) return;

        const nav = remoteNavLookup.get(key);
        if (!nav) {
          (LogService.addErrorLogAsync as any)(
            "",
            `Cloud sync download: remote nav not found for key ${key}`,
            "",
            "error",
            false,
            true
          );
          progressRemainingRef.current.download = Math.max(0, progressRemainingRef.current.download - 1);
          return;
        }
        const [points, source] = await pointCollection.getPointsByNavIdAsync(appUser?.uid, key, true, true);

        if (points.length > 1) {
          const pointsArray = Array.isArray(points) ? points : ConvertUtil.convertArrayObjectToArray(points);
          const fixedPointsArray = fixDuplicatePoints(pointsArray);

          if (!nav.usr || nav.usr !== appUser?.uid || !nav.ID) {
            nav.ID = key;
            nav.usr = appUser?.uid;
            nav.lmd = now.getTime() / 1000;
            nav.lmdFull = now.toISOString();
          }

          if (nav.tt !== undefined && (typeof nav.tt !== "number" || isNaN(nav.tt))) {
            nav.tt = 0;
            nav.lmd = now.getTime() / 1000;
            nav.lmdFull = now.toISOString();
          }

          try {
            const res = await dbService.setPointsForNavId(key, fixedPointsArray);
            if (res) {
              if (source && source === 1) {
                await navCollection.updateSingleKeyNavigationAsync(userId, {fileSynced: true}, key);
              }

              delete nav.fileSynced;
              delete nav.key;

              if (await dbService.setNavigation(nav)) {
                navigationsBatch.push(nav);
                if (navigationsBatch.length >= BATCH_FLUSH_SIZE) {
                  flushBatch();
                }
                if (!demoCleanupDone) {
                  demoCleanupDone = true;
                  await deleteExamplesActivities();
                }
              } else {
                (LogService.addErrorLogAsync as any)("", "Error while setNavigation " + key, "", "error", false, true);
              }
            } else {
              (LogService.addErrorLogAsync as any)(
                "",
                "Error while setPointsForNavId " + key,
                "",
                "error",
                false,
                true
              );
            }
          } catch (e) {
            (LogService.addErrorLogAsync as any)(
              "",
              "Error while saving/syncing navigations/points in localDB",
              e,
              "error",
              false,
              true
            );
          }
        } else {
          (LogService.addErrorLogAsync as any)(
            "",
            "Error while saving/syncing not enough points",
            "",
            "error",
            false,
            true
          );
        }
        progressRemainingRef.current.download = Math.max(0, progressRemainingRef.current.download - 1);
      });

      // Flush any remaining downloaded navigations before computing stats.
      flushBatch();

      // 6. Update user stats if needed
      if (toUpdateLocalDBKeys.length > 0 || toUpdateRemoteDBKeys.length > 0) {
        const durationInSeconds = navigationsRef.current.reduce((acc, nav) => acc + nav.sT, 0);
        const maxSpeedInKm = navigationsRef.current.reduce((acc, nav) => Math.max(acc, nav.sM || 0), 0);
        const userStats = {
          "Stats-Activities": navigationsRef.current.length,
          "Stats-Dur-MS": durationInSeconds * 1000,
          "Stats-Dur": DateTimeUtil.convertSecondsToTime(durationInSeconds, TIME_FORMATS.HHMMSS),
          "Stats-Dis": navigationsRef.current.reduce((acc, nav) => acc + nav.dT, 0),
          "Stats-Maxspeed": maxSpeedInKm,
          lmd: now.getTime() / 1000,
          lmdFull: now.toISOString()
        };

        await userCollection.updateUserAsync(appUser.uid, userStats, true);
      }
    } catch (error) {
      (LogService.addErrorLogAsync as any)("", "Error during sync", error, "error", false, true);
    } finally {
      syncInProgressRef.current = false;
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      const {upload, download} = progressRemainingRef.current;
      setNavToUpload(upload);
      setNavToDownload(download);
      setIsSyncing(false);
      pendingCloudSyncRef.current = false;
      setTimeout(() => {
        void fetchLocalData().catch((refreshError) => {
          (LogService.addErrorLogAsync as any)(
            "",
            "Cloud sync refresh local list failed",
            refreshError,
            "error",
            false,
            true
          );
        });
      }, 0);
    }
  };

  // Retry only when a sync was deferred (diary not ready yet or no network).
  useEffect(() => {
    if (!isAndroid || !appUser?.uid || !pendingCloudSyncRef.current || syncInProgressRef.current) {
      return;
    }
    if (!lastLocalNavForSyncRef.current || !canSync()) {
      return;
    }
    scheduleCloudSyncRef.current();
  }, [appUser?.uid, networkStatus, diaryActiveTick]);

  const requestCloudSync = useCallback(() => {
    pendingCloudSyncRef.current = true;
    scheduleCloudSyncRef.current();
  }, []);

  const fetchData = async () => {
    let navigations = (await navCollection.getNavigationsByUserIdAsync(userId || appUser.uid, true, true)) ?? [];
    navigations = ConvertUtil.convertArrayObjectToArray(navigations);

    if (!isAndroid) {
      addNavigationsToState(navigations, false, true);
      setIsNavigationsLoaded(true);
    }

    return navigations;
  };

  const values = useMemo(
    () => ({
      isNavigationsLoaded,
      navigations,
      setForceReload,
      navToUpload,
      navToDownload,
      addNavigationsToState,
      refreshLocalNavigations,
      requestCloudSync,
      isSyncing
    }),
    [
      isNavigationsLoaded,
      navigations,
      navToUpload,
      navToDownload,
      addNavigationsToState,
      refreshLocalNavigations,
      requestCloudSync,
      isSyncing
    ]
  );

  return <NavigationsContext.Provider value={values}>{children}</NavigationsContext.Provider>;
}

function useNavigations() {
  return useContext(NavigationsContext);
}

export {NavigationsProvider, useNavigations};
